// src/auth/socialAuth.js
// LOOP AUTH OPTIMAL R31 — one social-auth implementation shared by Login and Register.

import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
  signInWithPopup,
} from "firebase/auth";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { firebaseAuth } from "../firebase";
import { httpRequestJson, isIosCapacitor } from "../utils/nativeHttp";
import {
  clearLoopAuthState,
  getManualAuth,
  isManualAuthLikelyValid,
  persistManualAuth,
} from "../utils/manualAuth";
import {
  FirebaseNative,
  getCurrentUserSession,
  isFirebaseNativeAvailable,
  refreshSession,
} from "../native/firebaseNative";

const LoopCredentialManager = registerPlugin("LoopCredentialManager");

const GOOGLE_WEB_CLIENT_ID =
  "357961918175-otucm5e15jcpjo7p3ht5e7nblv12ago8.apps.googleusercontent.com";

const LOOP_AUTH_FUNCTIONS_BASE =
  "https://us-central1-web-app-d9799.cloudfunctions.net";
const LOOP_RESOLVE_SOCIAL_PROFILE_URL = `${LOOP_AUTH_FUNCTIONS_BASE}/resolveAuthProfileHttpR31`;

const LOOP_PROFILE_COMPLETION_CACHE_PREFIX = "loop_profile_complete_r31:";

export function getProfileCompletionCache(uid = "") {
  try {
    const cleanUid = String(uid || "").trim();
    if (!cleanUid || typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(
      `${LOOP_PROFILE_COMPLETION_CACHE_PREFIX}${cleanUid}`,
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - Number(parsed.savedAt || 0) > 1000 * 60 * 30)
      return null;
    return parsed.complete === true;
  } catch (_) {
    return null;
  }
}

export function setProfileCompletionCache(uid = "", complete = false) {
  try {
    const cleanUid = String(uid || "").trim();
    if (!cleanUid || typeof window === "undefined") return;
    window.localStorage.setItem(
      `${LOOP_PROFILE_COMPLETION_CACHE_PREFIX}${cleanUid}`,
      JSON.stringify({ complete: complete === true, savedAt: Date.now() }),
    );
  } catch (_) {}
}

function withTimeout(promise, ms, label = "timeout") {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms);
  });

  return Promise.race([
    Promise.resolve(promise).finally(() => {
      try {
        if (timer) clearTimeout(timer);
      } catch (_) {}
    }),
    timeout,
  ]);
}

function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, "=");
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
}

function extractIdentity(idToken, fallback = {}) {
  const payload = decodeJwtPayload(idToken) || {};
  return {
    uid: fallback.uid || payload.user_id || payload.uid || payload.sub || null,
    email:
      fallback.email ||
      payload.email ||
      payload.firebase?.identities?.email?.[0] ||
      "",
    displayName:
      fallback.displayName || payload.name || payload.displayName || "",
    provider: fallback.provider || payload.firebase?.sign_in_provider || "",
  };
}

function broadcastAuthChange() {
  try {
    window.dispatchEvent(new Event("manual-auth-detected"));
    window.dispatchEvent(new Event("manual-auth-changed"));
    window.dispatchEvent(new Event("loop-authenticated"));
  } catch (_) {}
}

function persistTokenSession({
  idToken,
  refreshToken = null,
  uid = null,
  email = "",
  displayName = "",
  source = "social-auth",
  provider = "",
}) {
  if (!idToken) throw new Error("social-auth-missing-id-token");

  const identity = extractIdentity(idToken, {
    uid,
    email,
    displayName,
    provider,
  });

  persistManualAuth({
    idToken,
    refreshToken,
    expiresIn: "3600",
    uid: identity.uid,
    obtainedAt: new Date().toISOString(),
    source,
    email: identity.email,
    displayName: identity.displayName,
    provider: identity.provider || provider || "",
    emailVerified: decodeJwtPayload(idToken)?.email_verified === true,
  });

  broadcastAuthChange();
  return identity;
}

async function persistFirebaseUser(user, source, provider) {
  if (!user) throw new Error("social-auth-missing-firebase-user");
  const idToken = await user.getIdToken(false);
  const identity = persistTokenSession({
    idToken,
    refreshToken: user.refreshToken || null,
    uid: user.uid || null,
    email: user.email || "",
    displayName: user.displayName || "",
    source,
    provider,
  });

  return {
    idToken,
    uid: identity.uid,
    email: identity.email,
    displayName: identity.displayName,
    provider,
    source,
  };
}

export function getLoopAuthPlatform() {
  try {
    if (typeof Capacitor?.getPlatform === "function") {
      const importedPlatform = Capacitor.getPlatform();
      if (importedPlatform) return importedPlatform;
    }
  } catch (_) {}

  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    if (cap) {
      if (typeof cap.getPlatform === "function") return cap.getPlatform();
      if (cap.platform) return cap.platform;
    }
  } catch (_) {}

  return isIosCapacitor() ? "ios" : "web";
}
export function isAppleAuthAvailable() {
  return getLoopAuthPlatform() !== "android";
}

async function resolveSocialProfile(session) {
  const response = await withTimeout(
    httpRequestJson({
      url: LOOP_RESOLVE_SOCIAL_PROFILE_URL,
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.idToken}`,
      },
      data: {
        provider: session.provider,
        source: session.source,
        displayName: session.displayName || "",
      },
    }),
    30000,
    "resolve-social-profile-timeout",
  );

  if (!response || response.ok !== true) {
    throw new Error(response?.message || "resolve-social-profile-failed");
  }

  return response;
}

export async function resolveCurrentAuthProfile({
  source = "current_auth_profile_r31",
  provider = "",
  displayName = "",
  forceRefresh = false,
} = {}) {
  const idToken = await getCurrentAuthIdToken({ forceRefresh });
  const identity = extractIdentity(idToken, { provider, displayName });
  const profileResult = await resolveSocialProfile({
    idToken,
    uid: identity.uid,
    email: identity.email,
    displayName: displayName || identity.displayName || "",
    provider: provider || identity.provider || "",
    source,
  });

  if (identity.uid) {
    setProfileCompletionCache(
      identity.uid,
      profileResult.profileComplete === true,
    );
  }

  return profileResult;
}

async function finishSocialSession(session) {
  const profileResult = await resolveSocialProfile(session);
  setProfileCompletionCache(
    session.uid,
    profileResult.profileComplete === true,
  );
  return {
    ...session,
    profile: profileResult.profile || null,
    profileComplete: profileResult.profileComplete === true,
    isNewProfile: profileResult.isNewProfile === true,
  };
}

async function googleWeb() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const result = await withTimeout(
    signInWithPopup(firebaseAuth, provider),
    30000,
    "google-web-timeout",
  );
  return persistFirebaseUser(result.user, "web-sdk-google", "google.com");
}

async function googleAndroidCredentialManager() {
  clearLoopAuthState({ keepPendingRegistration: true });
  const result = await withTimeout(
    LoopCredentialManager.signInWithGoogle({
      serverClientId: GOOGLE_WEB_CLIENT_ID,
      filterByAuthorizedAccounts: false,
    }),
    30000,
    "google-credential-manager-timeout",
  );

  const idToken = result?.idToken ? String(result.idToken) : "";
  if (!idToken) throw new Error("google-credential-manager-missing-id-token");

  const credential = GoogleAuthProvider.credential(idToken);
  const firebaseResult = await withTimeout(
    signInWithCredential(firebaseAuth, credential),
    30000,
    "google-credential-manager-firebase-timeout",
  );

  return persistFirebaseUser(
    firebaseResult.user,
    "android-credential-manager-google",
    "google.com",
  );
}

async function googleAndroidFirebasePlugin() {
  clearLoopAuthState({ keepPendingRegistration: true });
  const nativeResult = await withTimeout(
    FirebaseAuthentication.signInWithGoogle({ scopes: ["profile", "email"] }),
    30000,
    "google-android-native-timeout",
  );

  const credentialData = nativeResult?.credential || {};
  const idToken =
    credentialData.idToken ||
    nativeResult?.idToken ||
    nativeResult?.user?.idToken ||
    nativeResult?.result?.idToken ||
    "";
  const accessToken =
    credentialData.accessToken ||
    nativeResult?.accessToken ||
    nativeResult?.user?.accessToken ||
    nativeResult?.result?.accessToken ||
    "";

  if (!idToken && !accessToken) {
    throw new Error("google-android-native-missing-token");
  }

  const credential = GoogleAuthProvider.credential(
    idToken || null,
    accessToken || null,
  );
  const firebaseResult = await withTimeout(
    signInWithCredential(firebaseAuth, credential),
    30000,
    "google-android-firebase-credential-timeout",
  );

  return persistFirebaseUser(
    firebaseResult.user,
    "android-native-google",
    "google.com",
  );
}

async function googleIosNative() {
  clearLoopAuthState({ keepPendingRegistration: true });
  if (!isFirebaseNativeAvailable()) {
    throw new Error("firebasenative-plugin-not-available");
  }

  const result = await withTimeout(
    FirebaseNative.signInWithGoogle(),
    30000,
    "google-ios-native-timeout",
  );

  const idToken = result?.idToken ? String(result.idToken) : "";
  if (!idToken) throw new Error("google-ios-native-missing-id-token");

  const identity = persistTokenSession({
    idToken,
    uid: result?.uid || null,
    email: result?.email || "",
    displayName: result?.displayName || "",
    source: "ios-native-google",
    provider: "google.com",
  });

  return {
    idToken,
    uid: identity.uid,
    email: identity.email,
    displayName: identity.displayName,
    provider: "google.com",
    source: "ios-native-google",
  };
}

async function appleWeb() {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  const result = await withTimeout(
    signInWithPopup(firebaseAuth, provider),
    30000,
    "apple-web-timeout",
  );
  return persistFirebaseUser(result.user, "web-sdk-apple", "apple.com");
}

async function appleIosNative() {
  clearLoopAuthState({ keepPendingRegistration: true });
  if (!isFirebaseNativeAvailable()) {
    throw new Error("firebasenative-plugin-not-available");
  }
  if (!FirebaseNative || typeof FirebaseNative.signInWithApple !== "function") {
    throw new Error("apple-native-method-not-available");
  }

  const result = await withTimeout(
    FirebaseNative.signInWithApple(),
    30000,
    "apple-ios-native-timeout",
  );

  const idToken = result?.idToken ? String(result.idToken) : "";
  if (!idToken) throw new Error("apple-ios-native-missing-id-token");

  const identity = persistTokenSession({
    idToken,
    uid: result?.uid || null,
    email: result?.email || "",
    displayName: result?.displayName || "",
    source: "ios-native-apple",
    provider: "apple.com",
  });

  return {
    idToken,
    uid: identity.uid,
    email: identity.email,
    displayName: identity.displayName,
    provider: "apple.com",
    source: "ios-native-apple",
  };
}

function shouldSkipGoogleFallback(error) {
  const raw = String(
    error?.code || error?.message || error || "",
  ).toLowerCase();
  return (
    raw.includes("account-exists-with-different-credential") ||
    raw.includes("already exists with the same email") ||
    raw.includes("different sign-in credentials") ||
    raw.includes("popup-closed") ||
    raw.includes("cancelled") ||
    raw.includes("canceled")
  );
}

export async function signInWithGoogleAcrossPlatforms() {
  const platform = getLoopAuthPlatform();
  let session;

  if (platform === "android") {
    try {
      session = await googleAndroidCredentialManager();
    } catch (credentialManagerError) {
      if (shouldSkipGoogleFallback(credentialManagerError)) {
        throw credentialManagerError;
      }
      console.warn(
        "[socialAuth] Credential Manager failed; using FirebaseAuthentication fallback",
        credentialManagerError,
      );
      session = await googleAndroidFirebasePlugin();
    }
  } else if (platform === "ios") {
    session = await googleIosNative();
  } else {
    session = await googleWeb();
  }

  return finishSocialSession({
    ...session,
    provider: "google.com",
    source: session.source || `${platform}-google`,
  });
}

export async function signInWithAppleAcrossPlatforms() {
  const platform = getLoopAuthPlatform();
  if (platform === "android") {
    throw new Error("apple-login-not-enabled-on-android");
  }

  const session =
    platform === "ios" ? await appleIosNative() : await appleWeb();
  return finishSocialSession({
    ...session,
    provider: "apple.com",
    source: session.source || `${platform}-apple`,
  });
}

export async function getCurrentAuthIdToken({ forceRefresh = false } = {}) {
  const platform = getLoopAuthPlatform();

  if (platform === "ios") {
    try {
      const manual = getManualAuth();
      if (!forceRefresh && manual?.idToken && isManualAuthLikelyValid(manual)) {
        return String(manual.idToken);
      }
    } catch (_) {}
  }

  try {
    const user = firebaseAuth?.currentUser || null;
    if (user && typeof user.getIdToken === "function") {
      return await user.getIdToken(Boolean(forceRefresh));
    }
  } catch (_) {}

  if (platform === "ios" && isFirebaseNativeAvailable()) {
    const result = forceRefresh
      ? await refreshSession()
      : await getCurrentUserSession();
    const idToken = result?.idToken ? String(result.idToken) : "";
    if (idToken) {
      persistTokenSession({
        idToken,
        uid: result?.uid || null,
        email: result?.email || "",
        displayName: result?.displayName || "",
        source: "ios-native-session-refresh",
        provider: result?.provider || "",
      });
      return idToken;
    }
  }

  throw new Error("auth-token-missing");
}

export function socialAuthErrorMessage(error) {
  const raw = String(
    error?.code || error?.message || error || "",
  ).toLowerCase();

  if (
    raw.includes("account-exists-with-different-credential") ||
    raw.includes("email-already-in-use") ||
    raw.includes("credential-already-in-use") ||
    raw.includes("already exists with the same email") ||
    raw.includes("different sign-in credentials")
  ) {
    return "Ya existe una cuenta con este correo usando otro método. Inicia sesión con ese método para mantener tus reservas y beneficios en una sola cuenta.";
  }
  if (
    raw.includes("popup-closed") ||
    raw.includes("cancelled") ||
    raw.includes("canceled")
  ) {
    return "Inicio de sesión cancelado.";
  }
  if (raw.includes("apple-login-not-enabled-on-android")) {
    return "Apple todavía no está disponible en Android. Usa Google o correo.";
  }
  if (raw.includes("network") || raw.includes("load failed")) {
    return "No pudimos conectarnos. Revisa tu internet e intenta nuevamente.";
  }
  if (raw.includes("timeout")) {
    return "La conexión tardó demasiado. Intenta nuevamente.";
  }
  if (raw.includes("firebasenative") || raw.includes("native-method")) {
    return "Esta versión de la app necesita una actualización para usar este acceso.";
  }
  if (raw.includes("resolve-social-profile")) {
    return "Iniciaste sesión, pero no pudimos preparar tu perfil. Intenta nuevamente.";
  }
  return "No pudimos continuar con ese método. Intenta nuevamente.";
}
